import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { fetchCompanyEliteMindUserDetail } from './companyEliteMindApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  ProgressList,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_COLORS,
  toneChip
} from '../../../../component/common/designSystem';

/**
 * Elite Mind → Athlete profile.
 *
 * Their sport and position, how regularly they keep the journal, and the
 * reports THEY CHOSE TO SHARE with the club — each as a date, a type and a
 * status.
 *
 * Two lines are drawn hard here and both are load-bearing:
 *
 *  1. Nothing from the journal appears. Not the symptoms, not the sleep, not
 *     the muscle tension, not whether they trained as planned, not the notes.
 *     The journal has no share control, and the app giving them an explicit one
 *     on reports and none here is the answer to whether it was meant to be
 *     read.
 *  2. A report they kept private is not counted, not hinted at and not
 *     reachable. A page that said "3 shared, 5 total" would defeat the switch
 *     entirely, so the endpoint behind this page never loads the other two.
 *
 * Even on a shared report, this shows the type and the status rather than the
 * clinical detail. Sharing a report with a medic inside the app is not the same
 * act as having its contents listed on a management screen.
 *
 * `fetcher`/`backPath`/`backLabel` are props so a super-admin Elite Mind hub can
 * reuse the page against the unscoped endpoint.
 */
const EliteMindUserDetail = ({
  fetcher = fetchCompanyEliteMindUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const byReportType = detail?.byReportType || [];
  const monthlyJournal = detail?.monthlyJournal || [];
  const reportHistory = detail?.reportHistory || [];

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard icon={STAT_ICONS.personalBest} title="Sport" value={user?.sportName || '-'} />
        <StatCard icon={STAT_ICONS.goals} title="Position" value={user?.position || '-'} />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Journal Entries"
          value={user ? user.journalEntries : '-'}
        />
        {/* Days out of the last 30 — a daily log only means something if it is
            actually daily. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Logged (30)"
          value={user ? user.recentDays : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Reports Shared"
          value={user ? user.sharedReports : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Last Entry"
          value={user?.lastEntryDate || '-'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="What They Share" subtitle="Shared reports by type">
          {user?.sharedReports > 0 ? (
            <ProgressList
              items={byReportType.map((row) => ({ label: row.label, count: row.count }))}
              unit="reports"
              color={CHART_COLORS.orange}
            />
          ) : (
            <PanelEmpty>This athlete has not shared any reports with the club.</PanelEmpty>
          )}
        </Panel>

        {/* A month and a count. Never an entry. */}
        <Panel title="Journal by Month" subtitle="How often they write it up">
          {monthlyJournal.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Month</th>
                  <th className="py-2 font-medium text-right">Entries</th>
                </>
              }
            >
              {monthlyJournal.map((row) => (
                <tr key={row.month} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.month}</td>
                  <td className="py-2 text-right">{row.entries}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No journal entries yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Athlete Summary" subtitle="The raw counts for this person">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Journal entries</td>
              <td className="py-2 text-right">{user ? user.journalEntries : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Separate days journalled</td>
              <td className="py-2 text-right">{user ? user.activeDays : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First journal entry</td>
              <td className="py-2 text-right">{user?.firstEntryDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Reports shared with the club</td>
              <td className="py-2 text-right">{user ? user.sharedReports : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Most recent shared report</td>
              <td className="py-2 text-right">{user?.lastReportDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      {/* A date, a type and a status. Never the clinical detail behind them. */}
      <div className="mt-6">
        <Panel
          title="Reports They Shared"
          subtitle="Most recent first — reports kept private are not listed or counted"
        >
          {reportHistory.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 px-4 font-medium text-center">Date of Event</th>
                  <th className="py-2 px-4 font-medium text-center">Logged</th>
                  <th className="py-2 font-medium text-right">Status Then</th>
                </>
              }
            >
              {reportHistory.map((row, index) => (
                <tr key={`${row.loggedOn}-${row.type}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.type}</td>
                  <td className="py-2 px-4 text-center">{row.reportDate || '-'}</td>
                  <td className="py-2 px-4 text-center">{row.loggedOn}</td>
                  <td className="py-2 text-right">{row.status || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This athlete has not shared any reports with the club.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

EliteMindUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

EliteMindUserDetail.defaultProps = {
  backLabel: 'Back to Athletes'
};

export default EliteMindUserDetail;
